// useDeviceDetect.js

import { useEffect, useState } from 'react';

import {
  COMPACT_SPLIT_MAX_WIDTH_PX,
  MOBILE_MAX_WIDTH_PX,
  PEEK_DOCK_MIN_WIDTH_PX,
  TABLET_SHELL_MAX_WIDTH_PX,
} from 'utilities/layoutBreakpoints';

const canMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const getInitialMatch = (query, defaultValue) => {
  if (!canMatchMedia()) return defaultValue;
  try {
    return window.matchMedia(query).matches;
  } catch (err) {
    return defaultValue;
  }
};

const useDeviceDetect = (query, defaultValue = false) => {
  const [matches, setMatches] = useState(() => getInitialMatch(query, defaultValue));

  useEffect(() => {
    if (!canMatchMedia()) return undefined;
    let mediaQueryList;
    try {
      mediaQueryList = window.matchMedia(query);
    } catch (err) {
      return undefined;
    }

    const updateMatch = (event) => setMatches(event.matches);

    setMatches(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', updateMatch);
      return () => mediaQueryList.removeEventListener('change', updateMatch);
    }

    if (typeof mediaQueryList.addListener === 'function') {
      mediaQueryList.addListener(updateMatch);
      return () => mediaQueryList.removeListener(updateMatch);
    }

    return undefined;
  }, [query]);

  return matches;
};

export const useMaxWidth = (widthPx, defaultValue = false) => {
  const query = `(max-width: ${widthPx}px)`;
  return useDeviceDetect(query, defaultValue);
};

export const useMinWidth = (widthPx, defaultValue = false) => {
  const query = `(min-width: ${widthPx}px)`;
  return useDeviceDetect(query, defaultValue);
};

export const useIsMobile = (
  breakpoint = MOBILE_MAX_WIDTH_PX,
  defaultValue = false,
) => useMaxWidth(breakpoint, defaultValue);

/**
 * True on the tablet / small-desktop band where the shell uses the icon rail
 * and Peek should overlay (viewport ≤ TABLET_SHELL_MAX_WIDTH_PX, not phone).
 */
export const useIsTabletShell = (defaultValue = false) => {
  const isMobile = useIsMobile(MOBILE_MAX_WIDTH_PX, defaultValue);
  const withinTabletShell = useMaxWidth(TABLET_SHELL_MAX_WIDTH_PX, defaultValue);
  return Boolean(withinTabletShell && !isMobile);
};

/**
 * Phone + tablet: embed the playbook wizard as the default surface.
 * Desktop (above the tablet shell) keeps the list and optional modal.
 */
export const useIsPlaybookFocusEmbed = (defaultValue = false) => (
  useMaxWidth(TABLET_SHELL_MAX_WIDTH_PX, defaultValue)
);

/** True when dense split panes should collapse (phone + mid-width tablet). */
export const useIsCompactSplit = (defaultValue = false) => (
  useMaxWidth(COMPACT_SPLIT_MAX_WIDTH_PX, defaultValue)
);

/** True when Peek may dock beside main-right instead of overlaying. */
export const useCanDockPeek = (defaultValue = false) => (
  useMinWidth(PEEK_DOCK_MIN_WIDTH_PX, defaultValue)
);

export {
  COMPACT_SPLIT_MAX_WIDTH_PX,
  MOBILE_MAX_WIDTH_PX,
  PEEK_DOCK_MIN_WIDTH_PX,
  TABLET_SHELL_MAX_WIDTH_PX,
};

export default useDeviceDetect;
